'use client';

import { useEffect, useRef, useState } from 'react';

interface Organelle {
  id: string;
  label: string;
  labelX: number;
  labelY: number;
  delay: number;
}

const organelles: Organelle[] = [
  { id: 'nucleus', label: 'Nucleus', labelX: 185, labelY: 118, delay: 400 },
  { id: 'chloroplast1', label: 'Chloroplast', labelX: 68, labelY: 148, delay: 1200 },
  { id: 'mitochondria', label: 'Mitochondria', labelX: 270, labelY: 190, delay: 2000 },
  { id: 'vacuole', label: 'Central Vacuole', labelX: 140, labelY: 210, delay: 2800 },
  { id: 'cell-wall', label: 'Cell Wall', labelX: 10, labelY: 60, delay: 3400 },
];

export default function PlantCellSVG() {
  const [drawnIds, setDrawnIds] = useState<Set<string>>(new Set());
  const [labelIds, setLabelIds] = useState<Set<string>>(new Set());
  const hasStarted = useRef(false);

  useEffect(() => {
    if (hasStarted.current) return;
    hasStarted.current = true;

    // Draw cell wall outline first
    setTimeout(() => {
      setDrawnIds(prev => new Set([...prev, 'outline']));
    }, 300);

    organelles.forEach(({ id, delay }) => {
      setTimeout(() => {
        setDrawnIds(prev => new Set([...prev, id]));
      }, delay);
      setTimeout(() => {
        setLabelIds(prev => new Set([...prev, id]));
      }, delay + 600);
    });
  }, []);

  return (
    <div className="animate-float">
      <svg
        viewBox="0 0 360 280"
        width="360"
        height="280"
        xmlns="http://www.w3.org/2000/svg"
        aria-label="Animated plant cell diagram showing organelles appearing one by one"
        role="img"
      >
        {/* Cell Wall outer */}
        <rect
          x="8" y="8" width="344" height="264" rx="24"
          fill="none"
          stroke="#4A7C59"
          strokeWidth="3.5"
          strokeDasharray="1200"
          strokeDashoffset={drawnIds.has('outline') ? 0 : 1200}
          style={{ transition: 'stroke-dashoffset 1.8s cubic-bezier(0.16,1,0.3,1)' }}
        />
        {/* Cell Membrane inner */}
        <rect
          x="16" y="16" width="328" height="248" rx="18"
          fill="rgba(234,242,236,0.45)"
          stroke="#6A9C79"
          strokeWidth="1.2"
          strokeDasharray="1100"
          strokeDashoffset={drawnIds.has('outline') ? 0 : 1100}
          style={{ transition: 'stroke-dashoffset 2s cubic-bezier(0.16,1,0.3,1) 0.3s' }}
        />

        {/* Nucleus */}
        <g>
          <circle
            cx="200" cy="130" r="38"
            fill="rgba(155,122,143,0.18)"
            stroke="#9B7A8F"
            strokeWidth="2"
            strokeDasharray="240"
            strokeDashoffset={drawnIds.has('nucleus') ? 0 : 240}
            style={{ transition: 'stroke-dashoffset 1.2s cubic-bezier(0.16,1,0.3,1)' }}
          />
          <circle
            cx="200" cy="130" r="14"
            fill="rgba(155,122,143,0.35)"
            stroke="#9B7A8F"
            strokeWidth="1.5"
            strokeDasharray="90"
            strokeDashoffset={drawnIds.has('nucleus') ? 0 : 90}
            style={{ transition: 'stroke-dashoffset 0.8s cubic-bezier(0.16,1,0.3,1) 0.5s' }}
          />
          {/* Nucleus label */}
          <text
            x="248" y="115"
            fontSize="9"
            fontFamily="DM Sans, sans-serif"
            fill="#9B7A8F"
            fontWeight="500"
            opacity={labelIds.has('nucleus') ? 1 : 0}
            style={{ transition: 'opacity 0.5s ease' }}
          >
            Nucleus
          </text>
          <line
            x1="238" y1="118" x2="222" y2="124"
            stroke="#9B7A8F" strokeWidth="0.8"
            opacity={labelIds.has('nucleus') ? 0.6 : 0}
            style={{ transition: 'opacity 0.5s ease' }}
          />
        </g>

        {/* Chloroplast 1 */}
        <g>
          <ellipse
            cx="82" cy="155" rx="28" ry="14"
            fill="rgba(74,124,89,0.22)"
            stroke="#4A7C59"
            strokeWidth="2"
            strokeDasharray="130"
            strokeDashoffset={drawnIds.has('chloroplast1') ? 0 : 130}
            style={{ transition: 'stroke-dashoffset 1s cubic-bezier(0.16,1,0.3,1)' }}
          />
          {/* Thylakoid lines */}
          {[0,1,2].map(i => (
            <line
              key={i}
              x1={68 + i * 7} y1="150" x2={68 + i * 7} y2="160"
              stroke="#4A7C59" strokeWidth="1.2"
              opacity={drawnIds.has('chloroplast1') ? 0.5 : 0}
              style={{ transition: `opacity 0.4s ease ${0.5 + i * 0.1}s` }}
            />
          ))}
          <text
            x="30" y="143"
            fontSize="9"
            fontFamily="DM Sans, sans-serif"
            fill="#4A7C59"
            fontWeight="500"
            opacity={labelIds.has('chloroplast1') ? 1 : 0}
            style={{ transition: 'opacity 0.5s ease' }}
          >
            Chloroplast
          </text>
          <line
            x1="60" y1="147" x2="68" y2="152"
            stroke="#4A7C59" strokeWidth="0.8"
            opacity={labelIds.has('chloroplast1') ? 0.6 : 0}
            style={{ transition: 'opacity 0.5s ease' }}
          />
        </g>

        {/* Chloroplast 2 */}
        <ellipse
          cx="130" cy="72" rx="22" ry="11"
          fill="rgba(74,124,89,0.18)"
          stroke="#4A7C59"
          strokeWidth="1.8"
          strokeDasharray="105"
          strokeDashoffset={drawnIds.has('chloroplast1') ? 0 : 105}
          style={{ transition: 'stroke-dashoffset 1s cubic-bezier(0.16,1,0.3,1) 0.4s' }}
        />

        {/* Mitochondria */}
        <g>
          <ellipse
            cx="282" cy="196" rx="22" ry="13"
            fill="rgba(212,160,60,0.18)"
            stroke="#D4A03C"
            strokeWidth="2"
            strokeDasharray="110"
            strokeDashoffset={drawnIds.has('mitochondria') ? 0 : 110}
            style={{ transition: 'stroke-dashoffset 1s cubic-bezier(0.16,1,0.3,1)' }}
          />
          {/* Cristae */}
          <path
            d="M270,193 Q276,189 282,193 Q288,197 294,193"
            fill="none" stroke="#D4A03C" strokeWidth="1"
            opacity={drawnIds.has('mitochondria') ? 0.6 : 0}
            style={{ transition: 'opacity 0.4s ease 0.6s' }}
          />
          <text
            x="300" y="194"
            fontSize="9"
            fontFamily="DM Sans, sans-serif"
            fill="#D4A03C"
            fontWeight="500"
            opacity={labelIds.has('mitochondria') ? 1 : 0}
            style={{ transition: 'opacity 0.5s ease' }}
          >
            Mitochondria
          </text>
        </g>

        {/* Central Vacuole */}
        <g>
          <ellipse
            cx="168" cy="190" rx="50" ry="40"
            fill="rgba(245,240,232,0.6)"
            stroke="#8C7E6A"
            strokeWidth="1.5"
            strokeDasharray="290"
            strokeDashoffset={drawnIds.has('vacuole') ? 0 : 290}
            style={{ transition: 'stroke-dashoffset 1.4s cubic-bezier(0.16,1,0.3,1)' }}
          />
          <text
            x="140" y="192"
            fontSize="8.5"
            fontFamily="DM Sans, sans-serif"
            fill="#8C7E6A"
            fontWeight="400"
            opacity={labelIds.has('vacuole') ? 1 : 0}
            style={{ transition: 'opacity 0.5s ease' }}
          >
            Central Vacuole
          </text>
        </g>

        {/* Cell wall label */}
        <g>
          <text
            x="20" y="58"
            fontSize="9"
            fontFamily="DM Sans, sans-serif"
            fill="#4A7C59"
            fontWeight="500"
            opacity={labelIds.has('cell-wall') ? 1 : 0}
            style={{ transition: 'opacity 0.5s ease' }}
          >
            Cell Wall
          </text>
          <line
            x1="20" y1="62" x2="20" y2="72"
            stroke="#4A7C59" strokeWidth="0.8"
            opacity={labelIds.has('cell-wall') ? 0.6 : 0}
            style={{ transition: 'opacity 0.5s ease' }}
          />
        </g>

        {/* Ribosome dots */}
        {[[110,110],[240,80],[300,140],[60,220],[320,230]].map(([cx, cy], i) => (
          <circle
            key={i}
            cx={cx} cy={cy} r="2.5"
            fill="#9B7A8F"
            opacity={drawnIds.has('mitochondria') ? 0.4 : 0}
            style={{ transition: `opacity 0.3s ease ${i * 0.15}s` }}
          />
        ))}
      </svg>
    </div>
  );
}